import React, { createContext, useState, useCallback, useContext } from 'react';
import PostArticle from 'views/posts/PostArticle';
import { ScheduledPostContext } from './ScheduledPostContext';
import { Notification, toast } from 'components/ui';
import { Loading } from 'components/shared';

export const PostArticleContext = createContext({
    openPostArticle: () => {},
    closePostArticle: () => {},
    openedPostGroup: null
});

export const PostArticleProvider = ({ children }) => {
    const [openedPostGroup, setOpenedPostGroup] = useState(null);
    const [isLoadingPostGroup, setIsLoadingPostGroup] = useState(false);
    const { getScheduledPostGroup } = useContext(ScheduledPostContext);

    /**
     * Open a post article modal with a post group.
     *
     * Saved post groups are hydrated from the full detail endpoint before opening:
     * the calendar/list payload is fetched with `paginate=false`, which returns the
     * lightweight `PostLightResource` shape that omits `description`, `preferences`,
     * etc. Rendering that directly leaves the editor preview empty, so we always
     * re-fetch the complete post group on open. New/unsaved posts (no `id`) already
     * carry their full payload in memory and open immediately.
     *
     * @param {Object} postGroup - The post group to open
     */
    const openPostArticle = useCallback(async (postGroup) => {
        if (!postGroup?.id) {
            setOpenedPostGroup(postGroup);
            return;
        }

        setIsLoadingPostGroup(true);
        try {
            const full = await getScheduledPostGroup(postGroup.id);
            setOpenedPostGroup(full ? { ...postGroup, ...full } : postGroup);
        } catch (err) {
            toast.push(<Notification title={err?.message} type="danger" />);
            setOpenedPostGroup(postGroup); // fall back; don't block the user
        } finally {
            setIsLoadingPostGroup(false);
        }
    }, [getScheduledPostGroup]);

    /**
     * Close the currently opened post article modal
     */
    const closePostArticle = useCallback(() => {
        setOpenedPostGroup(null);
    }, []);

    return (
        <PostArticleContext.Provider
            value={{
                openPostArticle,
                closePostArticle,
                openedPostGroup
            }}
        >
            {children}

            {isLoadingPostGroup && (
                <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50">
                    <Loading type="element" />
                </div>
            )}

            {/* Render PostArticle modal when there's an opened post group */}
            {openedPostGroup && (
                <PostArticle
                    key={openedPostGroup?.id || 1}
                    item={openedPostGroup}
                    forceFullScreen={true}
                    setOpenedPostGroup={setOpenedPostGroup}
                    showOptionsButton={false}
                />
            )}
        </PostArticleContext.Provider>
    );
};

export default PostArticleContext;

